'use client';

import type { PaidPlan } from '@/types/billing-plan';
import { Loader2 } from 'lucide-react';
import { useSearchParams } from 'next/navigation';
import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { usePricingActions } from '../hooks/use-pricing-actions';
import { getPlanComparisonCards } from '../plan-comparison';
import { PlanComparisonCards, PlanComparisonCardsSkeleton } from './plan-comparison-cards';
import { PricingPageShell } from './pricing-page-shell';
import { Button } from '@/components/ui/button';
import { NumericInput } from '@/components/ui/numeric-input';
import { useReviewLineLimits } from '@/data/billing/use-review-line-limits';
import { useSubscriptionStatus } from '@/data/billing/use-subscription-status';
import { useInstallationMembersStatistics } from '@/data/installations/use-installation-members-statistics';
import { trackClientEvent } from '@/lib/analytics/posthog-client';
import { countActiveDevelopersForProSeatMinimum, PRO_PLAN_ACTIVE_DEVELOPER_MIN_PRS, PRO_PLAN_ACTIVITY_WINDOW_DAYS } from '@/lib/billing/pro-seat-policy';
import { SETTINGS_TABS } from '@/lib/constants';
import { baseUrl } from '@/lib/env';
import { isPaidPlan, isPaidPlanUpgrade, isProTierPlan, PAID_PLANS, PRO_PLAN_CONTACT_EMAIL } from '@/types/billing-plan';

// Without a usable ?plan, offer the next plan up. Max has nothing above it, so it offers Pro.
const DEFAULT_TARGET_PLAN: Record<PaidPlan, PaidPlan> = {
  team: 'pro',
  pro: 'max',
  max: 'pro'
};
function getDefaultTargetPlan(planParam: string | null, currentPlan: PaidPlan | undefined): PaidPlan | null {
  if (isPaidPlan(planParam) && planParam !== currentPlan) {
    return planParam;
  }
  return currentPlan ? DEFAULT_TARGET_PLAN[currentPlan] : null;
}
export function PlanChangeView() {
  const searchParams = useSearchParams();
  const installationId = searchParams.get('installation_id');
  const planParam = searchParams.get('plan');
  const parsedInstallationId = Number(installationId) || undefined;
  const {
    isLoading,
    setIsLoading,
    startPlanChangeFlow
  } = usePricingActions();
  const {
    data: subscriptionStatus,
    isError: isSubscriptionStatusError
  } = useSubscriptionStatus(Number(installationId) || null, {
    live: true
  });
  const {
    data: membersStatisticsData,
    isPending: isMembersStatisticsLoading
  } = useInstallationMembersStatistics(parsedInstallationId);
  const {
    data: reviewLineLimits
  } = useReviewLineLimits(parsedInstallationId);
  const activeDeveloperCountForPro = countActiveDevelopersForProSeatMinimum(membersStatisticsData?.memberStatistics);
  // Guard: moves to Pro or Max can right-size seats, but they still cannot go
  // below the activity-based Pro minimum.
  const minimumSeats = Math.max(1, activeDeveloperCountForPro);
  const currentSeats = subscriptionStatus?.quantity;

  // The current plan on the subscription
  const currentPlan = subscriptionStatus?.paidPlan ?? undefined;
  const [pickedPlan, setPickedPlan] = useState<PaidPlan | null>(null);
  const [showDowngradeConfirmation, setShowDowngradeConfirmation] = useState(false);
  // Undefined until the admin edits the seat count. Null while the field is empty.
  const [editedSeats, setEditedSeats] = useState<number | null>();
  const confirmationSectionRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (installationId) {
      trackClientEvent('change_plan_page_viewed', installationId);
    }
  }, [installationId]);
  const targetPlan = pickedPlan ?? getDefaultTargetPlan(planParam, currentPlan);
  const billingCycle = subscriptionStatus?.billingInterval === 'year' ? 'yearly' : 'monthly';
  const planComparisonCards = getPlanComparisonCards({
    billingCycle,
    selectedPlan: targetPlan ?? 'team',
    mode: 'change',
    currentPlan,
    reviewLineLimits
  });
  const isUpgrade = Boolean(currentPlan && targetPlan && isPaidPlanUpgrade(currentPlan, targetPlan));
  // Pro and Max need a seat count, including when Max moves down to Pro.
  const needsSeats = targetPlan !== null && isProTierPlan(targetPlan);
  const isLoadingSeatMinimum = needsSeats && isMembersStatisticsLoading;
  const isDowngrade = Boolean(currentPlan && targetPlan && isPaidPlanUpgrade(targetPlan, currentPlan));
  // Note: default to the current paid quantity, raised to the Pro minimum, so
  // the default CTA never silently lowers the subscription.
  const defaultSeats = Math.max(currentSeats ?? 0, minimumSeats);
  const resolveSeats = (edited: number | null | undefined) => {
    if (edited === undefined) {
      return defaultSeats;
    }
    if (edited === null) {
      return null;
    }
    return Math.max(edited, minimumSeats);
  };
  const seats = resolveSeats(editedSeats);
  const displayedSeats = seats ?? minimumSeats;
  const isSeatCountInvalid = needsSeats && (seats === null || !Number.isFinite(seats) || seats < minimumSeats);
  const targetPlanName = PAID_PLANS[targetPlan ?? 'team'].name;

  // NumericInput steps from the value it shows, so resolve its updaters against the displayed count.
  const handleSeatsChange: Dispatch<SetStateAction<number | null>> = update => {
    setEditedSeats(previous => {
      const current = resolveSeats(previous);
      return typeof update === 'function' ? update(current) : update;
    });
  };
  const handleCtaClick = (plan: PaidPlan) => {
    if (plan === currentPlan) {
      return;
    }
    if (plan !== targetPlan) {
      setEditedSeats(undefined);
    }
    setPickedPlan(plan);
    // Note: upgrades render inline immediately, but downgrades still require a separate confirmation step.
    setShowDowngradeConfirmation(!!currentPlan && isPaidPlanUpgrade(plan, currentPlan));
    confirmationSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const handleConfirm = async () => {
    if (!installationId || !targetPlan) {
      toast.error('Missing installation or target plan.');
      return;
    }
    if (isSeatCountInvalid) {
      toast.error(`Select at least ${minimumSeats} ${minimumSeats === 1 ? 'seat' : 'seats'} for ${targetPlanName}.`);
      return;
    }
    if (isLoadingSeatMinimum) {
      toast.error(`Still loading your current seats and ${targetPlanName} minimum. Please wait a moment and try again.`);
      return;
    }
    setIsLoading(true);
    try {
      const result = await startPlanChangeFlow({
        installationId: Number.parseInt(installationId, 10),
        targetPlan,
        // Note: only Pro and Max targets send an explicit quantity. Downgrades to
        // Team continue to use the server-side plan change behavior unchanged.
        ...(needsSeats && typeof seats === 'number' ? {
          quantity: seats
        } : {})
      });
      if (result.action === 'payment_action_required') {
        if (result.redirectUrl) {
          window.location.href = result.redirectUrl;
          return;
        }
        toast.error(result.message ?? `Complete payment to finish upgrading to ${targetPlanName}.`);
        return;
      }
      const changeUrl = new URL(`${baseUrl()}/settings`);
      changeUrl.searchParams.set('tab', SETTINGS_TABS.BILLING);
      changeUrl.searchParams.set('plan_change_success', 'true');
      changeUrl.searchParams.set('plan_change_action', result.action);
      window.location.href = changeUrl.toString();
    } catch (error) {
      const errorMessage = error instanceof Error && error.message ? error.message : 'Failed to change plan. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };
  const seatCountField = <div className="space-y-2">
      <p className="text-sm text-zinc-400">Number of developer seats</p>
      <NumericInput value={seats} setValue={handleSeatsChange} min={minimumSeats} />
      <p className="text-sm text-zinc-500">
        {`Minimum: ${minimumSeats} ${minimumSeats === 1 ? 'seat' : 'seats'}`}
      </p>
    </div>;
  return <PricingPageShell title="Change your plan" containerClassName="max-w-screen-xl lg:px-8">
      {subscriptionStatus ? <PlanComparisonCards cards={planComparisonCards} selectedPlan={targetPlan} currentPlan={currentPlan} onCtaClick={handleCtaClick} /> : !parsedInstallationId ? <p className="text-center text-sm text-muted-foreground">
          Missing installation. Please go back to settings and select an installation.
        </p> : isSubscriptionStatusError ? <p className="text-center text-sm text-muted-foreground">
          Couldn&apos;t load your current plan. Refresh the page to try again.
        </p> : <PlanComparisonCardsSkeleton />}

      <div ref={confirmationSectionRef}>
        {isUpgrade && <div className="mx-auto mt-8 max-w-3xl rounded-xl border border-border bg-[#101011] p-4 text-left sm:p-8">
            <div className="space-y-6">
              <div className="space-y-3">
                <h3 className="text-2xl font-medium">{`Upgrade to ${targetPlanName}`}</h3>
                <p className="text-base text-zinc-400">
                  {isLoadingSeatMinimum ? `Checking your current seats and ${targetPlanName} minimum based on recent PR activity.` : `${targetPlanName} requires at least ${minimumSeats} ${minimumSeats === 1 ? 'seat' : 'seats'} for this installation, based on ${activeDeveloperCountForPro} active ${activeDeveloperCountForPro === 1 ? 'developer' : 'developers'} (${PRO_PLAN_ACTIVE_DEVELOPER_MIN_PRS}+ PRs in the last ${PRO_PLAN_ACTIVITY_WINDOW_DAYS} days).`}
                </p>
              </div>
              {!isLoadingSeatMinimum && <>
                  {seatCountField}
                  <p className="text-base text-zinc-400">
                    {'This takes effect immediately with prorated billing. '}
                    <a className="underline" href={`mailto:${PRO_PLAN_CONTACT_EMAIL}`}>
                      Contact us
                    </a>
                    {' for exceptions.'}
                  </p>
                  <div className="flex">
                    <Button variant="default" disabled={isLoading || isSeatCountInvalid} onClick={handleConfirm}>
                      {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {`Upgrade to ${targetPlanName} with ${displayedSeats} ${displayedSeats === 1 ? 'seat' : 'seats'}`}
                    </Button>
                  </div>
                </>}
            </div>
          </div>}

        {showDowngradeConfirmation && isDowngrade && <div className="mx-auto mt-8 max-w-3xl rounded-xl border border-border bg-[#101011] p-4 text-left sm:p-8">
            <div className="space-y-6">
              <div className="space-y-3">
                <h3 className="text-2xl font-medium">{`Downgrade to ${targetPlanName}`}</h3>
                <p className="text-base text-zinc-400">
                  This takes effect at the end of your current billing period. You will continue to
                  have access to your current plan until then.
                </p>
              </div>
              {needsSeats && !isLoadingSeatMinimum && seatCountField}
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowDowngradeConfirmation(false)}>
                  Cancel
                </Button>
                <Button variant="default" disabled={isLoading || isLoadingSeatMinimum || isSeatCountInvalid} onClick={handleConfirm}>
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Confirm downgrade
                </Button>
              </div>
            </div>
          </div>}
      </div>
    </PricingPageShell>;
}